import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { identify, track } from '../analytics';
import { setSentryUser } from '../sentry';

const LEGACY_TEAMS_KEY = 'pokemon-champions-my-teams';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setLoading(false);
      setSentryUser(u);
      if (u) {
        identify(u.uid, { signed_in: true });
        // One-shot migration of localStorage teams -> Firestore on first sign-in
        // (only when the user has no teams in Firestore yet).
        try {
          await migrateLegacyTeams(u.uid);
        } catch (e) {
          console.warn('Team migration skipped:', e);
        }
      }
    });
    return unsub;
  }, []);

  const signIn = useCallback(
    async (email, password) => {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      track('sign_in', { method: 'email' });
      return cred;
    },
    []
  );

  const signUp = useCallback(async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && cred.user) {
      await updateProfile(cred.user, { displayName });
    }
    track('sign_up', { method: 'email' });
    return cred;
  }, []);

  const signInWithGoogle = useCallback(
    async () => {
      const cred = await signInWithPopup(auth, googleProvider);
      track('sign_in', { method: 'google' });
      return cred;
    },
    []
  );

  const signOut = useCallback(() => { track('sign_out'); return fbSignOut(auth); }, []);

  const value = { user, loading, signIn, signUp, signInWithGoogle, signOut };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

async function migrateLegacyTeams(uid) {
  let legacy;
  try {
    const raw = localStorage.getItem(LEGACY_TEAMS_KEY);
    legacy = raw ? JSON.parse(raw) : [];
  } catch {
    return;
  }
  if (!Array.isArray(legacy) || legacy.length === 0) return;

  const teamsCol = collection(db, 'users', uid, 'teams');
  const existing = await getDocs(teamsCol);
  if (!existing.empty) {
    // User already has cloud teams; don't overwrite. Leave localStorage alone.
    return;
  }

  const batch = writeBatch(db);
  for (const t of legacy) {
    if (!t || !t.id) continue;
    const ref = doc(teamsCol, String(t.id));
    batch.set(ref, {
      name: t.name || 'Untitled',
      pokemon: t.pokemon || [],
      updatedAt: t.updatedAt || new Date().toISOString(),
      migratedAt: new Date().toISOString(),
    });
  }
  await batch.commit();
  localStorage.removeItem(LEGACY_TEAMS_KEY);
}
